import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, SmartphoneIcon, LinkIcon, UnlinkIcon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
interface RaastRelinkDelinkProps {
  onBack: () => void;
  onHome: () => void;
}
type Step = 'list' | 'review' | 'otp' | 'success';
type ActionType = 'link' | 'delink';
export function RaastRelinkDelink({
  onBack,
  onHome
}: RaastRelinkDelinkProps) {
  const {
    bgStyle,
    cardBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('list');
  const [direction, setDirection] = useState(1);
  const [actionType, setActionType] = useState<ActionType>('link');
  const [selectedId, setSelectedId] = useState<any>(null);
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const mockIds = [{
    id: '03001234567',
    status: 'linked',
    account: PAY_FROM_ACCOUNTS[0]
  }, {
    id: '03119876543',
    status: 'unlinked',
    account: null
  }];
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`relink-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`relink-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2468">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2469">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2470" />
      </button>
      <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2471">{title}</h1>
      <button onClick={onHome} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isDark ? 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408] text-[#5A1408] hover:bg-[#5A1408]/5'}`} data-id="element-2472">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2473" />
      </button>
    </div>;
  const renderList = () => <motion.div key="list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2474">
      {renderHeader('Relink / Delink', onBack)}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2475">
        <p className={`text-[14px] mb-6 ${textSec}`} data-id="element-2476">
          Manage your RAAST IDs linked to this account.
        </p>
        <div className="space-y-4" data-id="element-2477">
          {mockIds.map((item, i) => <div key={i} className={`rounded-2xl p-5 border ${cardBg} ${borderClr}`} data-id="element-2478">
              <div className="flex items-center justify-between mb-4" data-id="element-2479">
                <div className="flex items-center gap-3" data-id="element-2480">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2481">
                    <SmartphoneIcon className="w-5 h-5 text-[#F37024]" data-id="element-2482" />
                  </div>
                  <div data-id="element-2483">
                    <p className={`text-[16px] font-bold ${textPri}`} data-id="element-2484">
                      {item.id}
                    </p>
                    <p className={`text-[12px] ${textSec}`} data-id="element-2485">
                      {item.status === 'linked' ? `Linked to ${item.account?.number.slice(-4)}` : 'Unlinked'}
                    </p>
                  </div>
                </div>
              </div>
              <button onClick={() => {
            setSelectedId(item);
            setActionType(item.status === 'linked' ? 'delink' : 'link');
            navigateTo('review');
          }} className={`w-full py-3 rounded-xl font-bold text-[14px] flex items-center justify-center gap-2 border-2 transition-colors ${item.status === 'linked' ? 'border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10' : 'border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10'}`} data-id="element-2486">
                {item.status === 'linked' ? <>
                    <UnlinkIcon className="w-4 h-4" data-id="element-2487" /> Delink ID
                  </> : <>
                    <LinkIcon className="w-4 h-4" data-id="element-2488" /> Link ID
                  </>}
              </button>
            </div>)}
        </div>
      </div>
    </motion.div>;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2489">
      {renderHeader('Confirm Action', () => navigateTo('list', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2490">
        <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2491">
          <h3 className={`text-[16px] font-bold mb-6 text-center ${textPri}`} data-id="element-2492">
            Confirm {actionType === 'link' ? 'Linking' : 'Delinking'}
          </h3>
          <div className="space-y-4" data-id="element-2493">
            <div className="flex justify-between" data-id="element-2494">
              <span className={textSec} data-id="element-2495">RAAST ID</span>
              <span className={`font-bold ${textPri}`} data-id="element-2496">{selectedId?.id}</span>
            </div>
            <div className="flex justify-between" data-id="element-2497">
              <span className={textSec} data-id="element-2498">Action</span>
              <span className={`font-bold ${actionType === 'link' ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-2499">
                {actionType === 'link' ? 'Link to Account' : 'Delink from Account'}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('otp')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2500">
          Confirm
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2501">
      {renderHeader('Enter OTP', () => navigateTo('review', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2502">
        <div className="mb-8 text-center" data-id="element-2503">
          <p className={`text-[14px] ${textSec}`} data-id="element-2504">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2505">
          {otp.map((digit, i) => <input key={i} id={`relink-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-xl text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2506" />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2507">
          {timer > 0 ? <p className={`text-[14px] font-medium ${textSec}`} data-id="element-2508">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[14px] font-bold text-[#F37024]" data-id="element-2509">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  const renderSuccess = () => <motion.div key="success" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2510">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12" data-id="element-2511">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 15
      }} className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-[#22C55E]/30" data-id="element-2512">
          <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-2513" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-2xl font-bold mb-2 text-center ${textPri}`} data-id="element-2514">
          Success!
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-[15px] text-center mb-8 ${textSec}`} data-id="element-2515">
          Your RAAST ID has been successfully{' '}
          {actionType === 'link' ? 'linked' : 'delinked'}.
        </motion.p>
      </div>
      <div className="px-6 pb-8" data-id="element-2516">
        <button onClick={onHome} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2517">
          Done
        </button>
      </div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2518">
      {step === 'list' && renderList()}
      {step === 'review' && renderReview()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}